import { Chip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Popover } from "components/Popover/Popover";
import { useMemo } from "react";
import { ContextMenuWrapper } from "components/ContextMenu/ContextMenu";
import { CustomLinkMarkdown } from "components/Form/components/Markdown/Markdown";
import { Icon, IconLike } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import RecordLabel from "components/RecordLabel";
import { RuntimeProgressBar } from "components/RuntimeProgressBar/RuntimeProgressBar";
import "./executions.scss";
import { ExecutionStatusBadge } from "components/StatusBadge/ExecutionStatusBadge";
import { TimeAgo } from "components/TimeAgo/TimeAgo";
import { getExecutionMode } from "config/ts-utils";
import {
    InTrashBadge,
    PendingDeleteBadge,
    PendingRestoreBadge,
    PendingTrashBadge,
} from "containers/Resources/components/Badges/Badges";
import badgeStyle from "containers/Resources/components/Badges/Badges.module.scss";
import { StateBadges } from "containers/Resources/components/Badges/StateBadges";
import { getRecordColumnCellStyle } from "components/Table/recordColumnWidths";
import InlineActionButtons from "containers/Resources/components/CommonActions/InlineActionButtons";
import {
    EdgeKind,
    edgeKindLabels,
} from "containers/Resources/screens/ResourcesSingle/components/Visualization/DependencyVisualization/dependencyVisualization.service";
import { getRelativeUrl } from "utils/service";
import { statusColors } from "utils/utils";
import { resourceByName } from "./resources";
import { createdAtDto, createdByDto, modifiedAtDto, modifiedByDto } from "./utils";
const executionTypeIconMapping = {
    flow: IconCollection.codeTags,
    script: IconCollection.codeTags,
    connection: IconCollection.connection,
    schedule: IconCollection.timetable,
    wrapper: IconCollection.codeBraces,
};

type IconMapping = {
    [key: string]: IconLike;
};

export const connectorTypeIconMapping: IconMapping = {
    aws: IconCollection.aws,
    git: IconCollection.git,
    google: IconCollection.google,
    imap: IconCollection.emailReceiveOutline,
    k8s: IconCollection.kubernetes,
    ldap: IconCollection.accountGroup,
    mysql: IconCollection.database,
    ps: IconCollection.powershell,
    redis: IconCollection.memory,
    rest: IconCollection.web,
    scp: IconCollection.contentCopy,
    smb: IconCollection.contentCopy,
    smtp: IconCollection.emailSendOutline,
    soap: IconCollection.handWashOutline,
    sqlmssql: IconCollection.database,
    sqloracle: IconCollection.database,
    sqlpg: IconCollection.database,
    ssh: IconCollection.consoleLine,
    vault: IconCollection.safe,
    xmlrpc: IconCollection.xml,
};

const ENDED_STATUSES = new Set(["ENDED_SUCCESS", "ENDED_ERROR", "ENDED_CANCELLED"]);

// The Info cell has two lines by design: line 1 says WHAT ran (flow, connector,
// schedule, ...), line 2 is one state line whose parts are joined with " · "
// ("running since 7 minutes · ~47 seconds est. · Line 95 sleep"). Every
// RecordTable row has a fixed height, so a third stacked line would be cut
// through the middle. Each part carries `info-cell-part`; the separator is
// drawn in CSS so a part that renders nothing leaves no stray dot.
// (operator-task-fe-execution-info-cell-third-line-clipped)

const getRuntimeInfo = (execution: any) => {
    const { id, status, started_at } = execution;
    const isInFlight = started_at && !ENDED_STATUSES.has(status);
    if (!isInFlight) return null;
    return (
        <>
            <span className="info-cell-part">
                {status === "AT_ENGINE" ? (
                    <>
                        running since <TimeAgo datetime={started_at} />
                    </>
                ) : (
                    status
                )}
            </span>
            <RuntimeProgressBar
                executionId={id}
                startedAt={started_at}
                inline
                className="info-cell-part"
            />
        </>
    );
};

const getDependencyWaitingInfo = (execution: any) => {
    const { dependency_id, dependency_id_dependency } = execution;
    if (!dependency_id) return null;
    const dependencyCount =
        dependency_id_dependency?.dependency_execution_list_dependency_id
            ?.dependency_execution_count;
    return (
        <span className="info-cell-part">
            waits for {dependencyCount} dependenc{dependencyCount === 1 ? "y" : "ies"}
        </span>
    );
};

const getLockWaitingInfo = (execution: any) => {
    const { setting_id, setting_id_setting } = execution;
    if (!setting_id) return null;
    return (
        <span className="info-cell-part">
            waits for lock
            <RecordLabel
                link={getRelativeUrl("setting", setting_id)}
                name={setting_id_setting?.name}
                icon={resourceByName.setting.icon}
            />
        </span>
    );
};

const getResponseWaitingInfo = (execution: any) => {
    const { message_id, message_id_message } = execution;
    if (!message_id) return null;
    return (
        <span className="info-cell-part">
            waits for message
            <RecordLabel
                link={getRelativeUrl("message", message_id)}
                name={message_id_message?.subject}
                icon={resourceByName.message.icon}
            />
        </span>
    );
};

const getScheduledForText = (execution: any) => {
    const { scheduled_for, setting_id, dependency_id, message_id, status } = execution;
    if (!scheduled_for || status === "PAUSED") return null;
    return (
        <>
            {setting_id || dependency_id || message_id ? "timeout" : "runs"}{" "}
            <TimeAgo datetime={scheduled_for} />
        </>
    );
};

const getScheduledForPart = (execution: any) => {
    const text = getScheduledForText(execution);
    if (!text) return null;
    return <span className="info-cell-part">{text}</span>;
};

export const getScheduledForInfo = (execution: any, setNewValue?: any) => {
    const { scheduled_for } = execution;
    const text = getScheduledForText(execution);
    if (!text) return null;

    const getChildren = () => (
        <div className="info-cell-row">
            <div className="additionalInfo">{text}</div>
        </div>
    );

    if (setNewValue) {
        return (
            <ContextMenuWrapper
                handleClick={() =>
                    setNewValue({
                        field: "scheduled_for",
                        value: scheduled_for,
                    })
                }
            >
                {getChildren()}
            </ContextMenuWrapper>
        );
    } else {
        return getChildren();
    }
};

const getFlowInfo = (execution: any) => {
    const { flow_id, flow_id_flow } = execution;
    if (flow_id) {
        return (
            <div className="info-cell-row">
                <RecordLabel
                    link={getRelativeUrl("flow", flow_id)}
                    name={flow_id_flow?.name}
                    icon={executionTypeIconMapping.flow}
                />
            </div>
        );
    }
    return (
        <div className="info-cell-row">
            <Icon icon={executionTypeIconMapping.flow} /> flow
        </div>
    );
};

const getConnectionInfo = (execution: any) => {
    const { connector_id, connector_id_connector, connector_type } = execution;
    // TODO (low) refactor once dependency is typesafe
    const connectorType: keyof typeof connectorTypeIconMapping = connector_type.toLowerCase();
    const connectorIcon = connectorTypeIconMapping[connectorType];
    if (connector_id) {
        // One line, so the state line below still fits the row height.
        return (
            <div className="info-cell-row info-cell-connection">
                <Icon icon={connectorIcon} />
                <span className="info-cell-connection-type">
                    {connector_type.toLowerCase()} connection to
                </span>
                <RecordLabel
                    link={getRelativeUrl("connector", connector_id)}
                    name={connector_id_connector?.name}
                    icon={resourceByName.connector.icon}
                />
            </div>
        );
    }
    return (
        <div className="info-cell-row">
            <Icon icon={connectorIcon} /> {connector_type.toLowerCase()} connection
        </div>
    );
};

const getScheduleInfo = (execution: any) => {
    const { schedule_id, schedule_id_schedule } = execution;
    if (schedule_id) {
        return (
            <div className="info-cell-row">
                <RecordLabel
                    link={getRelativeUrl("schedule", schedule_id)}
                    name={schedule_id_schedule?.name}
                    icon={executionTypeIconMapping.schedule}
                />
            </div>
        );
    }
    return (
        <div className="info-cell-row">
            <Icon icon={executionTypeIconMapping.schedule} /> schedule
        </div>
    );
};

const getWrapperInfo = (execution: any) => {
    const { wrapper_id, wrapper_id_wrapper } = execution;
    if (wrapper_id) {
        return (
            <div className="info-cell-row">
                <RecordLabel
                    link={getRelativeUrl("wrapper", wrapper_id)}
                    name={wrapper_id_wrapper?.name}
                    icon={executionTypeIconMapping.wrapper}
                />
            </div>
        );
    }
    return (
        <div className="info-cell-row">
            <Icon icon={executionTypeIconMapping.wrapper} /> wrapper
        </div>
    );
};

const getScriptInfo = (execution: any) => {
    return (
        <div className="info-cell-row">
            <Icon icon={executionTypeIconMapping.script} /> script
        </div>
    );
};

const getIsRemoteProcessedInfo = (execution: any) => {
    if (!execution?.sync_config_id) return null;
    const sync_config_link = (
        <RecordLabel
            link={getRelativeUrl("sync_config", execution?.sync_config_id)}
            name={execution?.sync_config_id_identity?.name}
            icon={resourceByName.sync_config.icon}
        />
    );
    if (execution?.is_remote_processed) {
        return <span className="info-cell-part">running remotely in {sync_config_link}</span>;
    }
    return <span className="info-cell-part">remotely started by {sync_config_link}</span>;
};

const getLineNumberInfo = (execution: any) => {
    if (!execution?.lineno) return null;
    return (
        <span className="info-cell-part">
            Line {execution.lineno} {execution.last_command}
        </span>
    );
};

export const executionInfoColumn = (setNewValue) => ({
    label: "Info",
    key: "info",
    dto: (data: any) => {
        const { type } = data;
        const runtimeInfo = getRuntimeInfo(data);
        const lockWaitingInfo = getLockWaitingInfo(data);
        const responseWaitingInfo = getResponseWaitingInfo(data);
        const dependencyWaitingInfo = getDependencyWaitingInfo(data);
        const scheduledForInfo = getScheduledForPart(data);
        const isRemoteProcessedInfo = getIsRemoteProcessedInfo(data);
        const lineNumberInfo = getLineNumberInfo(data);
        const hasState = [
            runtimeInfo,
            lockWaitingInfo,
            responseWaitingInfo,
            dependencyWaitingInfo,
            scheduledForInfo,
            isRemoteProcessedInfo,
            lineNumberInfo,
        ].some(Boolean);
        let typeInfo;
        switch (type) {
            case "FLOW":
                typeInfo = getFlowInfo(data);
                break;
            case "CONNECTION":
                typeInfo = getConnectionInfo(data);
                break;
            case "SCHEDULE":
                typeInfo = getScheduleInfo(data);
                break;
            case "WRAPPER":
                typeInfo = getWrapperInfo(data);
                break;
            case "SCRIPT":
                typeInfo = getScriptInfo(data);
                break;
            default:
                typeInfo = type;
                break;
        }
        return (
            <ContextMenuWrapper handleClick={() => setNewValue({ field: "type", value: type })}>
                <div className="info-cell">
                    {typeInfo}
                    {hasState && (
                        <div className="info-cell-state">
                            {runtimeInfo}
                            {lockWaitingInfo}
                            {responseWaitingInfo}
                            {dependencyWaitingInfo}
                            {scheduledForInfo}
                            {isRemoteProcessedInfo}
                            {lineNumberInfo}
                        </div>
                    )}
                </div>
            </ContextMenuWrapper>
        );
    },
    shown: true,
    sortable: false,
    cellStyle: getRecordColumnCellStyle("info"),
});

/**
 * Children-tab "Relation" column: how each descendant relates to the current execution
 * — child / dependency / both. Rendered as plain text (not a coloured pill): the relation
 * kind is secondary information in this list, so a coloured tag adds visual noise without
 * pulling its weight (per UI review). `kindMap` (execution id → EdgeKind) comes from the
 * backend `GET /execution/{id}/process/relations` endpoint. Rows without a tagged relation
 * render "—". Cell padding/vertical-centering live in ExecutionsListing.scss (`.relationCell`).
 */
const relationColumn = (kindMap: Record<string, EdgeKind>) => ({
    label: "Relation",
    key: "relation",
    dto: (data: any) => {
        const kind: EdgeKind | undefined = kindMap[data?.id];
        if (kind == null) {
            return <span className="relationCellEmpty">—</span>;
        }
        return <span className="relationCellValue">{edgeKindLabels[kind]}</span>;
    },
    shown: true,
    sortable: false,
});

// SINGLE source of truth for the execution "Name" column, consumed by BOTH the
// main Executions screen (executionsListingColumns, below) AND the AdvancedSearch
// "Executions" tab (executionColumns in advancedFilterColumns.tsx). Previously
// each surface defined its own Name column and they drifted: the main screen
// showed the exec-mode chip + trash badges but no comment-count / description /
// sort, while AdvancedSearch (via the generic commonColumns.name) was sortable
// and showed comment-count + description but lacked the exec-mode chip. This is
// the agreed SUPERSET that preserves every behaviour of both
// (operator-task-fe-dedup-executions-column-config, Stefan 2026-10-16): the
// RecordLabel carries name + description + comment-count, the StateBadges strip
// carries the exec-mode chip and the trash/pending badges, and the column is
// sortable. The generic resource badges (deprecation / syntax-error /
// bundle-content / read-only / bootstrap-user) and the inline-edit pencil that
// the generic commonColumns.name also renders are intentionally omitted here:
// they all self-gate to nothing for execution rows, so dropping them leaves the
// AdvancedSearch execution Name cell visually unchanged apart from gaining the
// exec-mode chip.
export const executionNameColumn = (setNewValue) => ({
    label: "Name",
    key: "name",
    dto: (data: any) => {
        const {
            id,
            name,
            description,
            comment_count,
            deleted_at,
            pending_trash_identity_id,
            pending_restore_identity_id,
            pending_delete_identity_id,
        } = data;
        return (
            <ContextMenuWrapper handleClick={() => setNewValue({ field: "name", value: name })}>
                <div className={badgeStyle.nameCellWithBadges}>
                    <RecordLabel
                        link={getRelativeUrl("execution", id)}
                        name={name}
                        description={description}
                        commentCount={comment_count}
                        icon={resourceByName.execution.icon}
                    />
                    <StateBadges>
                        {getExecutionMode(data) ? (
                            <Chip
                                label={getExecutionMode(data)}
                                size="small"
                                variant="outlined"
                                color="warning"
                            />
                        ) : null}
                        <InTrashBadge deletedAt={deleted_at} />
                        <PendingTrashBadge pendingTrashIdentityId={pending_trash_identity_id} />
                        <PendingRestoreBadge
                            pendingRestoreIdentityId={pending_restore_identity_id}
                        />
                        <PendingDeleteBadge pendingDeleteIdentityId={pending_delete_identity_id} />
                    </StateBadges>
                </div>
            </ContextMenuWrapper>
        );
    },
    shown: true,
    sortable: true,
    // Floor-only Name column (grow to the full execution name instead of a
    // fixed 260px clip), matching every other list view.
    // (operator-task-fe-list-view-name-column-width-cap-vs-clip)
    cellStyle: getRecordColumnCellStyle("name"),
});

// Shared styling for the execution status-message hover popover — defined ONCE so
// both execution surfaces get the identical popover (paper is interactive so the
// pointer can move into it; root is pointer-transparent). Call this hook at a
// component's top level and thread the returned classes into the column builders.
export const useExecutionStatusPopoverClasses = () => {
    const useStyles = useMemo(
        () =>
            makeStyles(() => ({
                paperStyle: {
                    overflow: "visible",
                    pointerEvents: "auto",
                    // The dark tooltip bubble + radius + shadow live on the
                    // `.status__tooltip` content div (SCSS, where getColor is
                    // available); neutralise the MUI Paper's default white bg +
                    // shadow so only the dark bubble shows, matching the app's
                    // standard rc-tooltip look (Margot 2026-10-20).
                    backgroundColor: "transparent",
                    boxShadow: "none",
                },
                rootStyle: {
                    pointerEvents: "none",
                },
            })),
        [],
    );
    const styles = useStyles();
    return { paper: styles.paperStyle, root: styles.rootStyle };
};

// SINGLE source of truth for the execution "Status" column, consumed by BOTH
// surfaces. Standardises on the app-standard components/Popover/Popover (MUI) for
// the status-message tooltip — the main Executions screen previously used an antd
// Popover (overlayClassName "statusCellPopover"), which is the one visible change
// on that surface from this dedup (operator-task-fe-dedup-executions-column-config).
// `paper`/`root` are the shared popover classes from useExecutionStatusPopoverClasses.
export const executionStatusColumn = (setNewValue, paper?: string, root?: string) => ({
    label: "Status",
    key: "status",
    dto: (data: any) => {
        const message = data?.message;
        // TODO (low) refactor once <data> is typesafe
        const status: keyof typeof statusColors = data?.status;
        const badge = <ExecutionStatusBadge data={data} />;
        return (
            <ContextMenuWrapper handleClick={() => setNewValue({ field: "status", value: status })}>
                <div className="statusCell">
                    {message ? (
                        <Popover
                            openOnAction="hover"
                            children={
                                <div>
                                    <CustomLinkMarkdown
                                        markdownString={message}
                                        className="statusMessageMarkdown"
                                    />
                                </div>
                            }
                            trigger={
                                <>
                                    {badge}
                                    <span className="statusMessage">{String(message).trim()}</span>
                                </>
                            }
                            contentClassName="status__tooltip"
                            classes={{ paper, root }}
                            // Keep the tip pointing at the status badge's centre even
                            // when MUI shifts the paper to stay in-viewport (otherwise
                            // the fixed-offset arrow drifts off the column), and open
                            // 12px ABOVE the badge so the downward arrow lands in the
                            // gap instead of plunging into the badge.
                            // (operator-task-fe-dedup-executions-column-config)
                            arrowTargetSelector=".status-badge--large"
                            anchorOrigin={{ vertical: -12, horizontal: "left" }}
                            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                        />
                    ) : (
                        <>{badge}</>
                    )}
                </div>
            </ContextMenuWrapper>
        );
    },
    shown: true,
    sortable: true,
});

// Priority and Actions columns, shared by the main Executions screen and the
// Advanced Search executions table so both offer the same columns.
export const executionPriorityColumn = {
    label: "Priority",
    key: "priority",
    dto: (data: any) => data.priority,
    shown: false,
    sortable: true,
};

export const executionActionsColumn = {
    // Dedicated Actions column (Margot, 2026-09-25,
    // operator-task-fe-list-view-row-height-harmonize-real): executions used
    // to nest the action buttons inside the Name cell, which the uniform
    // row-height clip then cut off at the bottom. Every other resource list
    // (SearchTabs, BundleManager) shows actions in a separate default-shown
    // column; executions now matches that convention.
    label: "Actions",
    key: "actions",
    cellStyle: getRecordColumnCellStyle("actions"),
    dto: (data: any, refreshAction: () => void) => (
        <InlineActionButtons
            resourceData={data}
            afterButtonAction={refreshAction}
            hideGoToParent
        />
    ),
    shown: true,
    sortable: false,
};

export const executionsListingColumns = (
    setNewValue,
    kindMap?: Record<string, EdgeKind>,
    statusPaper?: string,
    statusRoot?: string,
) => [
    executionNameColumn(setNewValue),
    executionInfoColumn(setNewValue),
    executionStatusColumn(setNewValue, statusPaper, statusRoot),
    ...(kindMap ? [relationColumn(kindMap)] : []),
    {
        label: "Created by",
        key: "created_by",
        dto: (data: any) => createdByDto(data, setNewValue),
        shown: true,
    },
    {
        label: "Created at",
        key: "created_at",
        dto: (data: any) => createdAtDto(data, setNewValue),
        shown: true,
        sortable: true,
    },
    {
        label: "Modified by",
        key: "modified_by",
        dto: (data: any) => modifiedByDto(data, setNewValue),
        shown: true,
    },
    {
        label: "Modified at",
        key: "modified_at",
        dto: (data: any) => modifiedAtDto(data, setNewValue),
        shown: true,
        sortable: true,
    },
    {
        label: "Scheduled for",
        key: "scheduled_for",
        dto: (data: any) => getScheduledForInfo(data, setNewValue),
        shown: false,
        sortable: true,
    },
    executionPriorityColumn,
    executionActionsColumn,
];

export const executionsActions = {
    shown: ["pause/resume", "cancel"],
    hidden: ["delete"],
};

// TODO (low) check whether this is used anywhere
export const executionsDetailsSidebar = [
    {
        label: "ID",
        valueKey: "id",
        dto: (value: any) => value,
    },
    {
        label: "Last Command",
        valueKey: "last_command",
        dto: (value: any) => value,
    },
    {
        label: "Parent Execution Link",
        valueKey: "",
        dto: (value: any) => value,
    },
    {
        label: "Child Execution Search",
        valueKey: "",
        dto: (value: any) => value,
    },
    {
        label: "Ended At",
        valueKey: "ended_at",
        dto: (value: any) => value,
    },
    {
        label: "Runtime",
        valueKey: "runtime",
        dto: (value: any) => value,
    },
    {
        label: "Link To Flow",
        valueKey: "flow_id",
        dto: (value: any) => value,
    },
    {
        label: "Link To Location",
        valueKey: "",
        dto: (value: any) => value,
    },
];
